/**
 * #900 Phase 2: Onboarding "Otros" — detect and apply feature overrides.
 *
 * When a user selects "Otros" and picks features that don't match the
 * resolved rubro's capabilities, this module:
 * 1. Maps selected feature ids to capability codes
 * 2. Calls `apply_onboarding_feature_overrides` RPC to add missing caps
 * 3. Returns the list of applied overrides so the caller can notify the user
 */
import { supabase } from '../../shared/supabase/supabaseClient';
import { featureIdsToCapabilityCodes } from './onboardingFeatureMapping';
import type { OtherFeatureOptionId } from './otherFeatureCatalog';

export type OnboardingOverrideResult = {
  applied: string[];
  skipped: string[];
  error?: string;
};

/**
 * Apply onboarding feature overrides for capabilities the rubro doesn't have.
 * The RPC is owner-only and idempotent. Does not throw — returns error in result.
 */
export async function applyOnboardingCapabilityDisables(
  capabilityCodes: readonly string[],
): Promise<void> {
  for (const code of capabilityCodes) {
    try {
      const { error } = await supabase.rpc('toggle_my_organization_capability', {
        p_capability_code: code,
        p_enabled: false,
      });
      if (error) {
        console.warn('[onboardingFeatureOverrides] disable RPC error', code, error);
      }
    } catch (e) {
      console.warn('[onboardingFeatureOverrides] disable exception', code, e);
    }
  }
}

export async function applyOnboardingFeatureOverrides(
  organizationId: string,
  featureIds: readonly OtherFeatureOptionId[],
): Promise<OnboardingOverrideResult> {
  const capabilityCodes = featureIdsToCapabilityCodes(featureIds);
  if (capabilityCodes.length === 0) {
    return { applied: [], skipped: [] };
  }

  try {
    const { data, error } = await supabase.rpc('apply_onboarding_feature_overrides', {
      p_org_id: organizationId,
      p_capability_codes: capabilityCodes,
    });

    if (error) {
      console.warn('[onboardingFeatureOverrides] RPC error', error);
      return { applied: [], skipped: capabilityCodes, error: error.message };
    }

    const result = data as { applied?: string[]; skipped?: string[] } | null;
    return {
      applied: result?.applied ?? [],
      skipped: result?.skipped ?? [],
    };
  } catch (e: any) {
    console.warn('[onboardingFeatureOverrides] exception', e);
    return { applied: [], skipped: capabilityCodes, error: e?.message ?? 'unknown' };
  }
}
